import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AppConst from "../apis/appconst";
import "bootstrap/dist/css/bootstrap.min.css";

/* =====================================================================
   Report Configuration  (Velzon / Bootstrap admin style)
   Edits every THEME field and saves it to the backend.
   Uses AppConst for the API base, axios + FormData, and toast,
   matching the rest of the app (e.g. Registrations.jsx).
   ===================================================================== */

const DEFAULTS = {
  headerBg: [217, 251, 220], headerTxt: [37, 92, 20], border: [37, 92, 20],
  flags: {
    high: { label: "H", color: [200, 30, 30] },
    low: { label: "L", color: [47, 83, 149] },
    borderline: { label: "B", color: [230, 126, 34] },
    bold: 1, boldColor: [0, 0, 0],
  },
  outline: { patientBox: 1, testTable: 1, deptHeader: 1, interpBox: 1, radius: 0.7 },
  barcode: { show: 1, width: 30, height: 6, gapTop: 1.3, showText: 1 },
  font: {
    deptHeader: 12, tableHead: 9, tableBody: 9, testName: 9, sectionTitle: 9,
    editorTitle: 10, patient: 10, barcodeText: 7, endOfReport: 12, pageNumber: 8,
  },
  qr: { show: 1, size: 17, gapTop: -3, gapRight: 3, scale: 4, margin: 0, ecLevel: "M" },
  genderRange: { enabled: 1, showOnlyMatched: 0 },
  columns: {
    testName: { width: 50, halign: "left" }, result: { width: 36, halign: "left" },
    unit: { width: 22, halign: "left" }, range: { width: 43, halign: "left" },
    method: { width: 40, halign: "left" },
  },
  printing: { printIfDue: 0, blockPrint: 0 },
  layout: { headerHeight: 30, topMarginWith: 40, topMarginWithout: 40, sideMargin: 14, signatureReserve: 40 },
};

const TARGET_COLS = 191;

const clamp = (n) => Math.max(0, Math.min(255, Math.round(+n || 0)));
const rgbToHex = (a = []) => "#" + [0, 1, 2].map((i) => clamp(a[i]).toString(16).padStart(2, "0")).join("");
const hexToRgb = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };

function mergeDefaults(base, over) {
  if (Array.isArray(base) || Array.isArray(over)) return over ?? base;
  if (typeof base !== "object" || base === null) return over ?? base;
  const out = { ...base };
  for (const k of Object.keys(over || {})) {
    out[k] = (typeof base[k] === "object" && base[k] !== null && !Array.isArray(base[k]))
      ? mergeDefaults(base[k], over[k]) : over[k];
  }
  return out;
}

/* ---------- small Bootstrap field helpers ---------- */
const NumField = ({ label, col = "col-md-4", value, onChange, step = 1, min }) => (
  <div className={col}>
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input type="number" className="form-control" step={step} min={min} value={value}
        onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))} />
    </div>
  </div>
);

const ColorField = ({ label, col = "col-md-4", value, onChange }) => (
  <div className={col}>
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="d-flex align-items-center gap-2">
        <input type="color" className="form-control form-control-color" value={rgbToHex(value)}
          onChange={(e) => onChange(hexToRgb(e.target.value))} />
        <span className="text-muted" style={{ fontSize: 12 }}>{rgbToHex(value)}</span>
      </div>
    </div>
  </div>
);

const ToggleField = ({ label, col = "col-md-4", value, onChange, id }) => (
  <div className={col}>
    <div className="form-check form-switch mb-3" style={{ marginTop: "2rem" }}>
      <input className="form-check-input" type="checkbox" role="switch" id={id}
        checked={!!value} onChange={(e) => onChange(e.target.checked ? 1 : 0)} />
      <label className="form-check-label" htmlFor={id}>{label}</label>
    </div>
  </div>
);

const SelectField = ({ label, col = "col-md-4", value, onChange, options }) => (
  <div className={col}>
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  </div>
);

/* ===================================================================== */
function ReportConfig({ currentUser = "", profile = "default" }) {
  const [theme, setTheme] = useState(() => structuredClone(DEFAULTS));
  const [status, setStatus] = useState("Loading…");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const patch = useCallback((fn) => {
    setTheme((prev) => { const next = structuredClone(prev); fn(next); return next; });
  }, []);

  const load = useCallback(async () => {
    setLoaded(false); setStatus("Loading…");
    try {
      const res = await axios.get(
        AppConst.BASE_URL + AppConst.getReportConfigUrl + "?key=" + encodeURIComponent(profile)
      );
      const data = res.data;
      if (data && data.theme) {
        setTheme(mergeDefaults(structuredClone(DEFAULTS), data.theme));
        setStatus(data.saved
          ? `Last saved${data.user ? " by " + data.user : ""} · ${data.saved}`
          : "No saved config yet — showing defaults");
      } else {
        setTheme(structuredClone(DEFAULTS));
        setStatus("Showing defaults");
      }
    } catch (err) {
      console.error("Report config load error:", err);
      setTheme(structuredClone(DEFAULTS));
      setStatus("Could not load — showing defaults");
    }
    setLoaded(true);
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("key", profile);
      fd.append("label", "Default Report Layout");
      fd.append("user", currentUser || "");
      fd.append("theme", JSON.stringify(theme));

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.saveReportConfigUrl, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data && res.data.status) {
        toast.success(res.data.success || "Report configuration saved");
        setStatus("Saved just now");
      } else {
        toast.error((res.data && res.data.error) || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Report config save error:", err);
      toast.error("Could not reach server");
    }
    setSaving(false);
  };

  const colTotal = Object.values(theme.columns).reduce((s, c) => s + (+c.width || 0), 0);
  const diff = colTotal - TARGET_COLS;
  const meterClass = Math.abs(diff) <= 3 ? "bg-success" : (diff > 3 ? "bg-danger" : "bg-warning");
  const meterNote = Math.abs(diff) <= 3 ? "Looks good"
    : diff > 3 ? `${Math.round(diff)} pt over ~191 — may overflow`
    : `${Math.round(-diff)} pt of empty space`;

  const fontLabels = {
    deptHeader: "Department band", tableHead: "Table headings", tableBody: "Table rows",
    testName: "Test name", sectionTitle: "Section titles", editorTitle: "Editor heading",
    patient: "Patient box", barcodeText: "Barcode text", endOfReport: "End of report", pageNumber: "Page number",
  };
  const colLabels = { testName: "Test name", result: "Result", unit: "Unit", range: "Normal range", method: "Method" };
  const flagMeta = [["high", "High (above range)"], ["low", "Low (below range)"], ["borderline", "Borderline (at limit)"]];

  const SaveButtons = () => (
    <>
      <button type="button" className="btn btn-light" onClick={load} disabled={!loaded || saving}>
        <i className="ri-refresh-line me-1" />Reload
      </button>
      &nbsp;&nbsp;
      <button type="button" className="btn btn-primary" onClick={save} disabled={!loaded || saving}>
        <i className="ri-save-line me-1" />{saving ? "Saving…" : "Save Configuration"}
      </button>
    </>
  );

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* header + top save */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h2 style={{ color: "#405189", marginBottom: 2 }}>Report Configuration</h2>
            <small className="text-muted">{status}</small>
          </div>
          <div><SaveButtons /></div>
        </div>

        <div className="row">
          {/* COLOURS */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Colours</h5>
                <div className="row">
                  <ColorField col="col-md-6" label="Header background" value={theme.headerBg} onChange={(a) => patch((t) => { t.headerBg = a; })} />
                  <ColorField col="col-md-6" label="Header text" value={theme.headerTxt} onChange={(a) => patch((t) => { t.headerTxt = a; })} />
                  <ColorField col="col-md-6" label="Border / lines" value={theme.border} onChange={(a) => patch((t) => { t.border = a; })} />
                  <ColorField col="col-md-6" label="Manual bold colour" value={theme.flags.boldColor} onChange={(a) => patch((t) => { t.flags.boldColor = a; })} />
                </div>
              </div>
            </div>
          </div>

          {/* FLAGS */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Result Flags</h5>
                <table className="table table-sm align-middle mb-2">
                  <thead className="table-light">
                    <tr><th>Flag</th><th style={{ width: 90 }}>Label</th><th style={{ width: 90 }}>Colour</th></tr>
                  </thead>
                  <tbody>
                    {flagMeta.map(([k, name]) => (
                      <tr key={k}>
                       <td>
  <div className="d-flex align-items-center gap-1">
    <input
      className="form-control form-control-sm"
      maxLength={3}
      style={{ width: 56 }}
      value={theme.flags[k].label}
      onChange={(e) => patch((t) => { t.flags[k].label = e.target.value; })}
    />
    <button type="button" className="btn btn-sm btn-outline-secondary px-2 py-0"
      title="Up arrow" onClick={() => patch((t) => { t.flags[k].label = "↑"; })}>↑</button>
    <button type="button" className="btn btn-sm btn-outline-secondary px-2 py-0"
      title="Down arrow" onClick={() => patch((t) => { t.flags[k].label = "↓"; })}>↓</button>
  </div>
</td>
                        <td>
                          <input className="form-control form-control-sm" maxLength={3} value={theme.flags[k].label}
                            onChange={(e) => patch((t) => { t.flags[k].label = e.target.value; })} />
                        </td>
                        <td>
                          <input type="color" className="form-control form-control-color form-control-sm" value={rgbToHex(theme.flags[k].color)}
                            onChange={(e) => patch((t) => { t.flags[k].color = hexToRgb(e.target.value); })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" role="switch" id="flagBold"
                    checked={!!theme.flags.bold} onChange={(e) => patch((t) => { t.flags.bold = e.target.checked ? 1 : 0; })} />
                  <label className="form-check-label" htmlFor="flagBold">Print flagged results in bold</label>
                </div>
              </div>
            </div>
          </div>

          {/* OUTLINES */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Outlines</h5>
                <div className="row">
                  <ToggleField col="col-md-6" id="oPatient" label="Patient details box" value={theme.outline.patientBox} onChange={(v) => patch((t) => { t.outline.patientBox = v; })} />
                  <ToggleField col="col-md-6" id="oTable" label="Test result table" value={theme.outline.testTable} onChange={(v) => patch((t) => { t.outline.testTable = v; })} />
                  <ToggleField col="col-md-6" id="oDept" label="Department header band" value={theme.outline.deptHeader} onChange={(v) => patch((t) => { t.outline.deptHeader = v; })} />
                  <ToggleField col="col-md-6" id="oInterp" label="Interpretation box" value={theme.outline.interpBox} onChange={(v) => patch((t) => { t.outline.interpBox = v; })} />
                  <NumField col="col-md-6" label="Corner radius" value={theme.outline.radius} step={0.1} min={0} onChange={(v) => patch((t) => { t.outline.radius = v; })} />
                </div>
              </div>
            </div>
          </div>

          {/* BARCODE */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Barcode</h5>
                <div className="row">
                  <ToggleField col="col-md-6" id="bcShow" label="Show barcode" value={theme.barcode.show} onChange={(v) => patch((t) => { t.barcode.show = v; })} />
                  <ToggleField col="col-md-6" id="bcText" label="Show UHID text" value={theme.barcode.showText} onChange={(v) => patch((t) => { t.barcode.showText = v; })} />
                  <NumField col="col-md-4" label="Width (pt)" value={theme.barcode.width} min={0} onChange={(v) => patch((t) => { t.barcode.width = v; })} />
                  <NumField col="col-md-4" label="Height (pt)" value={theme.barcode.height} min={0} onChange={(v) => patch((t) => { t.barcode.height = v; })} />
                  <NumField col="col-md-4" label="Gap top (pt)" value={theme.barcode.gapTop} step={0.1} onChange={(v) => patch((t) => { t.barcode.gapTop = v; })} />
                </div>
              </div>
            </div>
          </div>

          {/* QR */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>QR Code</h5>
                <div className="row">
                  <ToggleField col="col-md-6" id="qrShow" label="Show QR code" value={theme.qr.show} onChange={(v) => patch((t) => { t.qr.show = v; })} />
                  <NumField col="col-md-6" label="Size (pt)" value={theme.qr.size} min={0} onChange={(v) => patch((t) => { t.qr.size = v; })} />
                  <NumField col="col-md-4" label="Vertical offset" value={theme.qr.gapTop} step={0.5} onChange={(v) => patch((t) => { t.qr.gapTop = v; })} />
                  <NumField col="col-md-4" label="Gap right (pt)" value={theme.qr.gapRight} step={0.5} onChange={(v) => patch((t) => { t.qr.gapRight = v; })} />
                  <NumField col="col-md-4" label="Render scale" value={theme.qr.scale} min={1} onChange={(v) => patch((t) => { t.qr.scale = v; })} />
                  <NumField col="col-md-6" label="Quiet margin" value={theme.qr.margin} min={0} onChange={(v) => patch((t) => { t.qr.margin = v; })} />
                  <SelectField col="col-md-6" label="Error correction" value={theme.qr.ecLevel} options={["L", "M", "Q", "H"]} onChange={(v) => patch((t) => { t.qr.ecLevel = v; })} />
                </div>
              </div>
            </div>
          </div>

          {/* GENDER */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Gender Ranges</h5>
                <div className="row">
                  <ToggleField col="col-md-6" id="grEnabled" label="Auto-evaluate M/F ranges" value={theme.genderRange.enabled} onChange={(v) => patch((t) => { t.genderRange.enabled = v; })} />
                  <ToggleField col="col-md-6" id="grMatched" label="Print only matched range" value={theme.genderRange.showOnlyMatched} onChange={(v) => patch((t) => { t.genderRange.showOnlyMatched = v; })} />
                </div>
              </div>
            </div>
          </div>

          {/* FONTS */}
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Font Sizes <small className="text-muted">(pt)</small></h5>
                <div className="row">
                  {Object.keys(fontLabels).map((k) => (
                    <NumField key={k} col="col-md-3" label={fontLabels[k]} value={theme.font[k]} step={0.5} min={1} onChange={(v) => patch((t) => { t.font[k] = v; })} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* COLUMNS */}
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 style={{ color: "#405189" }}>Column Widths &amp; Alignment <small className="text-muted">(target ≈ 191 pt)</small></h5>
                <div className="table-responsive">
                  <table className="table table-nowrap align-middle mb-0">
                    <thead className="table-light">
                      <tr><th>Column</th><th style={{ width: 160 }}>Width (pt)</th><th style={{ width: 200 }}>Alignment</th></tr>
                    </thead>
                    <tbody>
                      {Object.keys(colLabels).map((k) => (
                        <tr key={k}>
                          <td>{colLabels[k]}</td>
                          <td>
                            <input type="number" min={0} className="form-control" value={theme.columns[k].width}
                              onChange={(e) => patch((t) => { t.columns[k].width = e.target.value === "" ? 0 : parseFloat(e.target.value); })} />
                          </td>
                          <td>
                            <select className="form-select" value={theme.columns[k].halign}
                              onChange={(e) => patch((t) => { t.columns[k].halign = e.target.value; })}>
                              {["left", "center", "right"].map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex align-items-center gap-3 mt-3">
                  <div className="progress flex-grow-1" style={{ height: 8 }}>
                    <div className={`progress-bar ${meterClass}`} style={{ width: `${Math.min(100, (colTotal / TARGET_COLS) * 100)}%` }} />
                  </div>
                  <span className="fw-bold">{Math.round(colTotal * 10) / 10} pt</span>
                  <span className="text-muted small">{meterNote}</span>
                </div>
              </div>
            </div>
          </div>

{/* REPORT PRINTING CONFIG */}
<div className="col-lg-12">
  <div className="card">
    <div className="card-body">
      <h5 style={{ color: "#405189" }}>Report Printing Config</h5>
      <div className="row">
        <ToggleField
          col="col-md-6"
          id="prtDue"
          label="Print report if due"
          value={theme.printing.printIfDue}
          onChange={(v) => patch((t) => { t.printing.printIfDue = v; })}
        />
        <ToggleField
          col="col-md-6"
          id="prtBlock"
          label="Do not allow report to print"
          value={theme.printing.blockPrint}
          onChange={(v) => patch((t) => { t.printing.blockPrint = v; })}
        />
      </div>
    </div>
  </div>
</div>


{/* REPORT MARGINS */}
<div className="col-lg-12">
  <div className="card">
    <div className="card-body">
      <h5 style={{ color: "#405189" }}>Report Margins <small className="text-muted">(pt)</small></h5>
      <div className="row">
        <NumField col="col-md-4" label="Header image height" value={theme.layout.headerHeight} step={1} min={0}
          onChange={(v) => patch((t) => { t.layout.headerHeight = v; })} />
        <NumField col="col-md-4" label="Top margin (with header)" value={theme.layout.topMarginWith} step={1} min={0}
          onChange={(v) => patch((t) => { t.layout.topMarginWith = v; })} />
        <NumField col="col-md-4" label="Top margin (without header)" value={theme.layout.topMarginWithout} step={1} min={0}
          onChange={(v) => patch((t) => { t.layout.topMarginWithout = v; })} />
        <NumField col="col-md-4" label="Side margin (L/R)" value={theme.layout.sideMargin} step={1} min={0}
          onChange={(v) => patch((t) => { t.layout.sideMargin = v; })} />
        <NumField col="col-md-4" label="Signature reserve" value={theme.layout.signatureReserve} step={1} min={0}
          onChange={(v) => patch((t) => { t.layout.signatureReserve = v; })} />
      </div>
    </div>
  </div>
</div>




        </div>

        {/* bottom save (same place your other pages put actions) */}
        <div className="card">
          <div className="card-body">
            <div className="text-end"><SaveButtons /></div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default ReportConfig;